/**
 * Utility script to update existing admin-uploaded documents to verified status
 * Run this once to fix documents that were uploaded before the auto-verify fix
 */

import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';

export const updateAdminDocumentsToVerified = async () => {
  try {
    console.log('🔧 Starting update of admin-uploaded documents to verified status...');

    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));

    let totalUpdated = 0;
    let batchCount = 0;

    // Process users in batches to avoid overwhelming Firestore
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      console.log(`📄 Processing documents for user: ${userId}`);

      // Get all documents for this user
      const documentsQuery = query(
        collection(db, 'users', userId, 'documents'),
        where('adminUpload', '==', true)
      );

      const documentsSnapshot = await getDocs(documentsQuery);

      if (documentsSnapshot.empty) {
        console.log(`  ➡️  No admin uploads found for user ${userId}`);
        continue;
      }

      // Create batch for this user's documents
      const batch = writeBatch(db);
      let userUpdates = 0;

      // eslint-disable-next-line no-loop-func
      documentsSnapshot.docs.forEach((docSnapshot) => {
        const docData = docSnapshot.data();

        // Only update documents that are currently pending and were admin uploads
        if (docData.status === 'pending' || docData.status === 'pending_verification') {
          const docRef = doc(db, 'users', userId, 'documents', docSnapshot.id);
          batch.update(docRef, {
            status: 'verified',
            updatedAt: new Date(),
            autoVerified: true,
            autoVerificationReason: 'Admin/facilitator upload - retroactively verified'
          });

          userUpdates++;
          totalUpdated++;

          console.log(`    ✅ Queued update for document: ${docData.category || docData.categoryId} (${docSnapshot.id})`);
        }
      });

      // Commit batch if there are updates
      if (userUpdates > 0) {
        await batch.commit();
        batchCount++;
        console.log(`  📦 Committed batch for user ${userId}: ${userUpdates} documents updated`);

        // Small delay between batches to be gentle on Firestore
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    console.log('✅ Update complete!');
    console.log(`📊 Summary:`);
    console.log(`  - Total documents updated: ${totalUpdated}`);
    console.log(`  - Users affected: ${batchCount}`);

    return {
      success: true,
      totalUpdated,
      usersAffected: batchCount
    };

  } catch (error) {
    console.error('❌ Error updating admin documents:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Function to check current status of admin uploads
export const checkAdminDocumentStatus = async () => {
  try {
    console.log('🔍 Checking current status of admin-uploaded documents...');

    const usersSnapshot = await getDocs(collection(db, 'users'));

    let totalAdminUploads = 0;
    let pendingCount = 0;
    let verifiedCount = 0;
    let otherCount = 0;

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;

      const documentsQuery = query(
        collection(db, 'users', userId, 'documents'),
        where('adminUpload', '==', true)
      );

      const documentsSnapshot = await getDocs(documentsQuery);

      // eslint-disable-next-line no-loop-func
      documentsSnapshot.docs.forEach((docSnapshot) => {
        const docData = docSnapshot.data();
        totalAdminUploads++;

        switch (docData.status) {
          case 'pending':
          case 'pending_verification':
            pendingCount++;
            break;
          case 'verified':
            verifiedCount++;
            break;
          default:
            otherCount++;
            break;
        }
      });
    }

    console.log('📊 Current admin upload status:');
    console.log(`  - Total admin uploads: ${totalAdminUploads}`);
    console.log(`  - Verified: ${verifiedCount}`);
    console.log(`  - Pending: ${pendingCount}`);
    console.log(`  - Other: ${otherCount}`);

    return {
      total: totalAdminUploads,
      verified: verifiedCount,
      pending: pendingCount,
      other: otherCount
    };

  } catch (error) {
    console.error('❌ Error checking admin document status:', error);
    return null;
  }
};